import { AddIcon } from "@minket/brand/icons/add";
import { KeysIcon } from "@minket/brand/icons/keys";
import { limits } from "@minket/core";
import { Badge } from "@minket/ui/components/badge";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { SealedValue } from "@minket/ui/components/sealed";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { cn } from "@minket/ui/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { useRecorded } from "@/components/audit/recorded";
import { count, stamp } from "@/components/ops/format";
import { When } from "@/components/ops/when";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { ProjectScope } from "@/components/page/scope";
import { TestModeStamp } from "@/components/page/stamp";
import { QueryView } from "@/components/page/states";
import { scopeLabels } from "@/components/tenancy/format";
import type { CreatedKey, CreateKeyBody } from "@/components/tenancy/keys.ondemand";
import { endpoints, type ResponseOf } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import type { Project } from "@/lib/queries";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { displayName } from "@/lib/session";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/keys")({
  component: Keys,
});

type Key = ResponseOf<typeof endpoints.keys>["data"][number];
type Environment = ResponseOf<typeof endpoints.environments>["data"][number];

const loadKeyDialog = () => import("@/components/tenancy/keys.ondemand");
const loadSign = () => import("@/components/tenancy/countersign.ondemand");
const CreateKeyDialog = lazy(() => loadKeyDialog().then((m) => ({ default: m.CreateKeyDialog })));
/** The slip shows only once a key is made, so it comes with the dialog that made it. */
const Slip = lazy(() => loadKeyDialog().then((m) => ({ default: m.Slip })));
const Sign = lazy(() => loadSign().then((m) => ({ default: m.Sign })));

const columns = ["Key", "Mode", "Scope", "Environment", "Rate", "Last used, UTC"];
const ghost = () => (
  <GhostTable
    columns={columns}
    label="API keys"
    widths={[undefined, "7rem", "9rem", "8rem", "7rem", "10rem"]}
  />
);

/** "A test key for the production environment": which key this is, before anyone pastes it. */
function describeKey(key: Key, environment: string | undefined): string {
  const mode = key.mode === "live" ? "A live key" : "A test key";
  return environment ? `${mode} for the ${environment.toLowerCase()} environment` : mode;
}

/**
 * The project's API keys (direction §7: "the slip at creation, display forms afterwards, revoke
 * as a countersign"). A new key is shown once, on a security slip above the ledger; copied, the
 * slip settles into its sealed display form, which is all the ledger ever shows of a key. A test
 * key's row carries the specimen overprint. Revoking is countersigned and lands in the audit log.
 */
function Keys() {
  const { project } = useWorkspace();
  const projectId = project?.id ?? "";
  const canManage = useCan("keys.manage");
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const environments = useQuery({
    ...apiQuery(endpoints.environments, { projectId }),
    enabled: project !== null,
  });
  const create = useMutation({
    mutationFn: (body: CreateKeyBody) => apiRequest(endpoints.createKey, { projectId }, { body }),
    onSuccess: (key) => {
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.keys, { projectId }) });
      setCreating(false);
      setCreated(key);
    },
  });
  const action = !project ? (
    <UnavailableAction label="Create key" icon={AddIcon} reason="Choose a project first" />
  ) : !canManage ? (
    <UnavailableAction label="Create key" icon={AddIcon} reason="Viewers cannot create keys" />
  ) : (
    <Button
      onClick={() => {
        create.reset();
        setCreating(true);
      }}
      onPointerEnter={() => void loadKeyDialog()}
      onFocus={() => void loadKeyDialog()}
    >
      <AddIcon size={20} aria-hidden />
      Create key
    </Button>
  );
  const environmentName = (id: string) =>
    environments.data?.data.find((env) => env.id === id)?.name;
  return (
    <Page
      title="Keys"
      description="Minket keeps only a key's hash, so a lost key must be revoked and replaced."
      action={action}
    >
      <ProjectScope
        will="Its keys appear here once one is made."
        icon={<KeysIcon />}
        preview={ghost}
      >
        {(current) => (
          <div className="grid min-w-0 content-start gap-6">
            {/* The tab names the page on screen; this names it in the outline, above the slip. */}
            <h2 className="sr-only">API keys</h2>
            {created?.token ? (
              <Suspense fallback={null}>
                <Slip
                  key={created.id}
                  title="Copy this key now"
                  meta={`${created.name}: ${describeKey(created, environmentName(created.environment_id))}.`}
                  secret={created.token}
                  noun="key"
                  sealedForm={created.display}
                  note="Minket stores only its hash, so it cannot show this key again. Put it where your app reads MINKET_API_KEY."
                  className="max-w-2xl"
                />
              </Suspense>
            ) : null}
            <KeyList
              project={current}
              environments={environments.data?.data ?? []}
              canManage={canManage}
            />
          </div>
        )}
      </ProjectScope>
      {creating && project ? (
        <Suspense fallback={null}>
          <CreateKeyDialog
            project={project}
            environments={environments.data?.data ?? []}
            create={create}
            onClose={() => setCreating(false)}
          />
        </Suspense>
      ) : null}
    </Page>
  );
}

function KeyList({
  project,
  environments,
  canManage,
}: {
  project: Project;
  environments: readonly Environment[];
  canManage: boolean;
}) {
  const projectId = project.id;
  const { user } = useWorkspace();
  const recorded = useRecorded();
  const query = useQuery(apiQuery(endpoints.keys, { projectId }, { limit: limits.pageSizeMax }));
  const restricted = query.data?.data.some((key) => key.domain_id !== null) ?? false;
  const domains = useQuery({
    ...apiQuery(endpoints.domains, { projectId }),
    enabled: restricted,
    retry: false,
  });
  const queryClient = useQueryClient();
  const [revoking, setRevoking] = useState<Key | null>(null);
  const revoke = useMutation({
    mutationFn: (key: Key) => apiRequest(endpoints.revokeKey, { projectId, keyId: key.id }),
    onSuccess: (_, key) => {
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.keys, { projectId }) });
      recorded(`${key.name} revoked.`, { action: "key.revoke", project_id: projectId });
      setRevoking(null);
    },
  });
  const envName = (id: string) => environments.find((env) => env.id === id)?.name;
  const domainName = (id: string) => domains.data?.data.find((domain) => domain.id === id)?.name;
  return (
    <QueryView query={query} noun="API keys" preview={ghost}>
      {(page) =>
        page.data.length === 0 ? (
          <EmptyState
            data-state="empty"
            icon={<KeysIcon />}
            title="No keys yet"
            description={`A key lets an app send as ${project.name}: live keys deliver, test keys land in test mode.`}
            preview={ghost()}
          />
        ) : (
          <div className="grid min-w-0 gap-3">
            <Table aria-label="API keys">
              <TableHeader>
                <TableRow>
                  {columns.map((column) => (
                    <TableHead
                      key={column}
                      className={cn(column === "Last used, UTC" && "text-right")}
                    >
                      {column}
                    </TableHead>
                  ))}
                  {canManage ? (
                    <TableHead className="w-0">
                      <span className="sr-only">Revoke</span>
                    </TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {page.data.map((key) => (
                  <TableRow
                    key={key.id}
                    className={cn("hover:bg-transparent", key.mode === "test" && "overprint")}
                  >
                    <TableCell className="min-w-48">
                      <span className="block font-semibold wrap-break-word">{key.name}</span>
                      <SealedValue className="mt-1.5">{key.display}</SealedValue>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {key.mode === "live" ? <Badge>Live</Badge> : <TestModeStamp />}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {scopeLabels[key.scope]}
                      {key.domain_id ? (
                        <span className="type-data block text-[12.5px] text-muted-foreground">
                          {domainName(key.domain_id) ?? "one domain"}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {envName(key.environment_id) ?? "Not loaded"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className="type-data">{count(key.rate_limit_per_second)}</span> a second
                    </TableCell>
                    <TableCell className="text-right">
                      <When value={key.last_used_at} empty="Never" />
                    </TableCell>
                    {canManage ? (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onPointerEnter={() => void loadSign()}
                          onClick={() => {
                            revoke.reset();
                            setRevoking(key);
                          }}
                        >
                          Revoke<span className="sr-only"> {key.name}</span>
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {page.has_more ? (
              <p className="text-sm text-muted-foreground">
                The newest {count(limits.pageSizeMax)} keys.
              </p>
            ) : null}
            {revoking ? (
              <Suspense fallback={null}>
                <Sign
                  title={`Revoke ${revoking.name}`}
                  description="Every request made with it is refused from now on. A revoked key cannot be restored."
                  plan={{
                    label: "The key to revoke",
                    columns: ["Action", "Key", "Last used"],
                    steps: [
                      {
                        key: revoking.id,
                        verdict: "Revoke",
                        subject: <SealedValue>{revoking.display}</SealedValue>,
                        detail: `${revoking.mode}, ${(envName(revoking.environment_id) ?? "").toLowerCase()}`,
                        reason: revoking.last_used_at
                          ? `${stamp(revoking.last_used_at)} UTC`
                          : "Never used",
                      },
                    ],
                  }}
                  act="Revoke key"
                  tone="destructive"
                  pending={revoke.isPending}
                  error={revoke.error}
                  signer={displayName(user)}
                  onConfirm={() => revoke.mutate(revoking)}
                  onClose={() => setRevoking(null)}
                />
              </Suspense>
            ) : null}
          </div>
        )
      }
    </QueryView>
  );
}
